import { useState } from 'react';
import { generateConversations } from '@/lib/mockData';
import { Conversation } from '@/lib/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import {
  MessageSquare,
  Phone,
  Globe,
  Bot,
  User,
  Send,
  CreditCard,
  AlertCircle,
  CheckCircle2,
  Clock,
  DollarSign,
  Calendar,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

const CHANNEL_ICONS = {
  sms: MessageSquare,
  voice: Phone,
  web: Globe,
};

const INTENT_LABELS = {
  eob_explanation: 'EOB Explanation',
  payment_plan: 'Payment Plan',
  dispute: 'Dispute',
  balance_inquiry: 'Balance Inquiry',
  insurance_question: 'Insurance Question',
};

const SENTIMENT_COLORS = {
  positive: 'bg-success',
  neutral: 'bg-warning',
  negative: 'bg-destructive',
};

export default function PatientConcierge() {
  const [conversations] = useState<Conversation[]>(() => generateConversations());
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(conversations[0]);
  const [aiMode, setAiMode] = useState(true);
  const [paymentPlanOpen, setPaymentPlanOpen] = useState(false);

  const getSentimentLabel = (score: number) => {
    if (score >= 0.6) return 'positive';
    if (score >= 0.4) return 'neutral';
    return 'negative';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Patient Concierge</h1>
          <p className="text-sm text-muted-foreground">
            AI-powered patient billing support
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-secondary/50">
            <Bot className="w-4 h-4 text-primary" />
            <span className="text-sm">AI Mode</span>
            <Switch
              checked={aiMode}
              onCheckedChange={setAiMode}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6 h-[calc(100vh-200px)]">
        {/* Conversation List */}
        <div className="col-span-4 glass-card-elevated overflow-hidden flex flex-col">
          <div className="p-4 border-b border-border">
            <h3 className="font-semibold text-sm">Active Conversations</h3>
            <p className="text-xs text-muted-foreground">{conversations.length} ongoing</p>
          </div>
          <div className="flex-1 overflow-auto scrollbar-thin">
            {conversations.map((conv) => {
              const ChannelIcon = CHANNEL_ICONS[conv.channel];
              const sentiment = getSentimentLabel(conv.sentiment);
              
              return (
                <div
                  key={conv.id}
                  onClick={() => setSelectedConversation(conv)}
                  className={cn(
                    "p-4 border-b border-border/50 cursor-pointer transition-colors",
                    selectedConversation?.id === conv.id 
                      ? "bg-primary/5 border-l-2 border-l-primary" 
                      : "hover:bg-secondary/50"
                  )}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                        <span className="text-xs font-medium">{conv.patientInitials}</span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{conv.patientInitials}</span>
                          <ChannelIcon className="w-3 h-3 text-muted-foreground" />
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {new Date(conv.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                    <Badge className={cn(
                      "text-[10px]",
                      conv.status === 'resolved' && "status-badge-success",
                      conv.status === 'escalated' && "status-badge-danger",
                      conv.status === 'active' && "status-badge-info"
                    )}>
                      {conv.status}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="text-[10px]">
                      {INTENT_LABELS[conv.intent]}
                    </Badge>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-muted-foreground">Sentiment</span>
                      <div className={cn("w-2 h-2 rounded-full", SENTIMENT_COLORS[sentiment])} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Conversation Detail */}
        <div className="col-span-5 glass-card-elevated overflow-hidden flex flex-col">
          {selectedConversation ? (
            <>
              {/* Header */}
              <div className="p-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                      <span className="font-medium">{selectedConversation.patientInitials}</span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{selectedConversation.patientInitials}</span>
                        <Badge variant="outline" className="text-xs">
                          {INTENT_LABELS[selectedConversation.intent]}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <DollarSign className="w-3 h-3" />
                        Balance: ${selectedConversation.balanceDue.toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    className="gap-2"
                    onClick={() => setPaymentPlanOpen(true)}
                  >
                    <CreditCard className="w-4 h-4" />
                    Payment Plan
                  </Button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-auto p-4 space-y-4 scrollbar-thin">
                {selectedConversation.messages.map((message) => (
                  <div
                    key={message.id}
                    className={cn(
                      "flex gap-3",
                      message.sender === 'patient' ? "justify-start" : "justify-end"
                    )}
                  >
                    {message.sender === 'patient' && (
                      <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-muted-foreground" />
                      </div>
                    )}
                    <div className={cn(
                      "max-w-[80%] p-3 rounded-lg",
                      message.sender === 'patient' 
                        ? "bg-secondary" 
                        : "bg-primary/10 border border-primary/20"
                    )}>
                      <p className="text-sm">{message.content}</p>
                      <span className="text-[10px] text-muted-foreground mt-1 block">
                        {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    {message.sender === 'agent' && (
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                        <Bot className="w-4 h-4 text-primary" />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Input */}
              <div className="p-4 border-t border-border">
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    placeholder={aiMode ? "Agent is handling... (override)" : "Type a message..."}
                    className="flex-1 h-10 px-4 bg-secondary/50 border border-border rounded-lg text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                  <Button size="icon">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              Select a conversation
            </div>
          )}
        </div>

        {/* Agent Console */}
        <div className="col-span-3 space-y-4">
          <div className="glass-card-elevated p-4">
            <div className="flex items-center gap-2 mb-4">
              <Bot className="w-4 h-4 text-primary" />
              <h3 className="font-semibold text-sm">Agent Console</h3>
            </div>
            
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-secondary/50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium">Mode</span>
                  <Badge className={aiMode ? "status-badge-success" : "status-badge-muted"}>
                    {aiMode ? 'AI-Powered' : 'Manual'}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  {aiMode 
                    ? "Agent will respond to patients automatically"
                    : "Human review required before responses"
                  }
                </p>
              </div>

              {selectedConversation && (
                <>
                  <div className="p-3 rounded-lg bg-secondary/50">
                    <span className="text-xs font-medium">Suggested Resolution</span>
                    <p className="text-sm mt-2">{selectedConversation.suggestedResolution}</p>
                  </div>

                  <div className="p-3 rounded-lg bg-secondary/50">
                    <span className="text-xs font-medium">Sentiment Analysis</span>
                    <div className="mt-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-muted-foreground">Score</span>
                        <span className="text-sm font-mono">{(selectedConversation.sentiment * 100).toFixed(0)}%</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className={cn(
                            "h-full rounded-full transition-all",
                            selectedConversation.sentiment >= 0.6 ? "bg-success" :
                            selectedConversation.sentiment >= 0.4 ? "bg-warning" : "bg-destructive"
                          )}
                          style={{ width: `${selectedConversation.sentiment * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div className="p-3 rounded-lg bg-warning/10 border border-warning/20">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-warning flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="text-xs font-medium text-warning">Compliance Note</span>
                    <p className="text-xs text-muted-foreground mt-1">
                      Agent will not provide clinical advice or discuss medical details.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="glass-card-elevated p-4">
            <h3 className="font-semibold text-sm mb-3">Quick Actions</h3>
            <div className="space-y-2">
              <Button variant="outline" size="sm" className="w-full justify-start gap-2">
                <CreditCard className="w-4 h-4" />
                Offer Payment Plan
              </Button>
              <Button variant="outline" size="sm" className="w-full justify-start gap-2">
                <CheckCircle2 className="w-4 h-4" />
                Mark Resolved
              </Button>
              <Button variant="outline" size="sm" className="w-full justify-start gap-2 text-warning">
                <AlertCircle className="w-4 h-4" />
                Escalate to Human
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Plan Modal */}
      <Dialog open={paymentPlanOpen} onOpenChange={setPaymentPlanOpen}>
        <DialogContent className="sm:max-w-[400px] bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-primary" />
              Payment Plan Builder
            </DialogTitle>
          </DialogHeader>
          
          {selectedConversation && (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-secondary/50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Total Due</span>
                  <span className="text-xl font-bold font-mono">
                    ${selectedConversation.balanceDue.toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="text-sm font-medium">Recommended Plan</h4>
                <div className="p-4 rounded-lg border border-primary/30 bg-primary/5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm">Monthly Payment</span>
                    <span className="text-lg font-bold font-mono text-primary">
                      ${(selectedConversation.balanceDue / 6).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>Duration</span>
                    <span>6 months</span>
                  </div>
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>Interest</span>
                    <span className="text-success">0%</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 p-3 rounded-lg bg-success/10 border border-success/20">
                <Calendar className="w-4 h-4 text-success" />
                <span className="text-sm">First payment due: Feb 1, 2026</span>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentPlanOpen(false)}>
              Cancel
            </Button>
            <Button className="gap-2">
              <Send className="w-4 h-4" />
              Send to Patient
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
